import type { ReactElement } from 'react';

// What kind of chat it is, and where it stands, as icons (the rail, the Chats list): one icon per kind of work, and a
// small status badge: waiting on you, working, done, with the Outlevo team.

export type ChatRowLike = {
  subject: 'workspace' | 'campaign' | 'task' | 'support'; kind?: string | null; status: string; campaign_status?: string | null;
  unfinished?: string | null; brief_state?: string | null; unread?: number;
  /** escalated to the Outlevo team (an open Support escalation) */
  team?: boolean;
};

const P = (d: string) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;
const KIND: Record<string, [string, string, string]> = {
  // kind: [label, icon path, colour class]
  prospect: ['Find companies', 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3', 'k-violet'],
  contacts: ['Find decision makers', 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8', 'k-teal'],
  import: ['Import a list', 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12', 'k-slate'],
  launch: ['Launch a campaign', 'M5 19c4-1 6-3 7-7l3-3a3 3 0 0 0-4-4l-3 3c-4 1-6 3-7 7zM14 6l4 4M5 19l2-5 3 3z', 'k-rose'],
  replies: ['Sort replies', 'M9 17H5l-2 3V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-6', 'k-sky'],
  waiting: ['What is waiting', 'M22 12h-6l-2 3h-4l-2-3H2M5.5 5.1 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.5-6.9A2 2 0 0 0 16.8 4H7.2a2 2 0 0 0-1.7 1.1z', 'k-amber'],
  analysis: ['Analyse campaigns', 'M3 3v18h18M7 15l4-4 3 3 5-6', 'k-amber'],
  week: ['This week', 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z', 'k-slate'],
  developer: ['Build with the API', 'M16 18l6-6-6-6M8 6l-6 6 6 6', 'k-violet'],
  general: ['Question', 'M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'k-slate'],
  campaign: ['Campaign', 'M22 2 11 13M22 2l-7 20-4-9-9-4z', 'k-green'],
  workspace: ['Workspace', 'M3 21h18M5 21V7l7-4 7 4v14', 'k-slate'],
  support: ['Support', 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4.9 4.9l4.3 4.3M14.8 14.8l4.3 4.3M19.1 4.9l-4.3 4.3M9.2 14.8l-4.3 4.3', 'k-rose'],
};

export function chatKind(c: ChatRowLike) {
  const k = c.subject === 'task' ? (c.kind ?? 'general') : c.subject;
  return KIND[k] ?? KIND.general!;
}

/** Where the chat stands, for the badge: waiting on you first, then the team, working, done. */
export function chatState(c: ChatRowLike): { key: 'you' | 'team' | 'working' | 'done' | 'open'; label: string } {
  if (c.team) return { key: 'team', label: c.unfinished === 'The Outlevo team replied' ? 'The Outlevo team replied' : 'With the Outlevo team' };
  if (c.unfinished === 'Waiting for your answer' || c.unfinished === 'A card waits for you' || c.brief_state === 'blocked') return { key: 'you', label: c.unfinished ?? 'Needs you' };
  if (c.brief_state === 'working' || c.unfinished === 'No answer yet' || c.campaign_status === 'building') return { key: 'working', label: 'Working' };
  if (c.brief_state === 'done' || c.campaign_status === 'active') return { key: 'done', label: c.campaign_status === 'active' ? 'Sending' : 'Done' };
  return { key: 'open', label: 'In progress' };
}

const BADGE: Record<string, ReactElement | null> = {
  you: P('M12 8v5M12 16.5h.01'),
  team: P('M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0'),
  working: P('M21 12a9 9 0 1 1-6.2-8.6'),
  done: P('M5 12.5l4.5 4.5L19 7.5'),
  open: null,
};

/** The type icon with its status badge. */
export function ChatGlyph({ c, size = 'md' }: { c: ChatRowLike; size?: 'sm' | 'md' }) {
  const [, d, tone] = chatKind(c);
  const st = chatState(c);
  return (
    <span className={`cg cg-${size} ${tone}`} aria-hidden="true">
      {P(d)}
      {BADGE[st.key] ? <span className={`cg-badge b-${st.key}`}>{BADGE[st.key]}</span> : null}
      {c.unread ? <span className="cg-unread" /> : null}
    </span>
  );
}
